import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Button } from "../components/ui/Button";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { ErrorText } from "../components/ui/feedback";
import { Field } from "../components/ui/Field";
import { Input } from "../components/ui/Input";
import { adminApi } from "./api";
import { Board } from "./dense";

/** Takes one media file down by its id, after a confirmation. Media ids are not listed here. */
export function TakedownForm() {
  const { t } = useI18n();
  const [mediaId, setMediaId] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);

  const takedown = useMutation({
    mutationFn: () => adminApi.takedownMedia(mediaId.trim(), reason.trim()),
    onSuccess: () => {
      setConfirming(false);
      setDone(true);
      setMediaId("");
      setReason("");
    },
  });

  const canSubmit = mediaId.trim().length > 0 && reason.trim().length > 0;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setDone(false);
    setConfirming(true);
  };

  return (
    <Board title={t("admin.takedown.title")}>
      <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-[1fr_2fr_auto] sm:items-end">
        <Field label={t("admin.takedown.mediaId")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={mediaId}
              onChange={(event) => setMediaId(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("admin.takedown.reason")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>
        <Button type="submit" variant="danger" disabled={!canSubmit || takedown.isPending}>
          {t("admin.takedown.submit")}
        </Button>
      </form>
      <ErrorText error={takedown.error} />
      {done ? (
        <p role="status" className="text-note text-ink-2">
          {t("admin.takedown.done")}
        </p>
      ) : null}

      {confirming ? (
        <ConfirmDialog
          title={t("admin.takedown.confirm.title")}
          message={t("admin.takedown.confirm.message", { id: mediaId.trim() })}
          confirmLabel={t("admin.takedown.submit")}
          variant="danger"
          busy={takedown.isPending}
          error={takedown.error}
          onCancel={() => setConfirming(false)}
          onConfirm={() => takedown.mutate()}
        />
      ) : null}
    </Board>
  );
}
